import React, { useState, useEffect, useRef } from "react";
import {
  Typography, TextField, Box, Button, CircularProgress,
  Grid, alpha, useTheme, useMediaQuery, InputAdornment,
  Stack, Chip, Paper,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import UserCard from "../components/UserCard";
import { addAUser, blockUser, searchUsers, updateUserRole } from "../app/services/users/userAPI";
import { IUser } from "../definitions/app/user.d";
import AddUserModal from "../components/AddUserModal";
import EditUserRoleModal from "../components/EditUserRoleModal";
import toast from "react-hot-toast";

const SkeletonCard = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        height: 130,
        bgcolor: alpha(theme.palette.grey[500], 0.04),
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: alpha(theme.palette.grey[500], 0.15) }} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ height: 14, width: "60%", bgcolor: alpha(theme.palette.grey[500], 0.15), borderRadius: 1, mb: 1 }} />
        <Box sx={{ height: 11, width: "80%", bgcolor: alpha(theme.palette.grey[500], 0.1), borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

const UserManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddUserModalOpen, setAddUserModalOpen] = useState(false);
  const [isEditRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const effectRan = useRef(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await searchUsers();
      setUsers(data || []);
    } catch {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (effectRan.current === false) {
      fetchUsers();
      return () => { effectRan.current = true; };
    }
  }, []);

  const handleToggleActive = async (user: IUser) => {
    try {
      const { data } = await blockUser(user.email);
      if (data?.status === "success") {
        toast.success(data.message || "User status updated");
        fetchUsers();
      } else {
        toast.error(data.message || "Failed to toggle status");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to toggle status");
    }
  };

  const handleRoleChange = async (user: IUser, newRole: string) => {
    try {
      const result = await updateUserRole(user.email, newRole);
      if (result?.status === "success") {
        toast.success("User role updated successfully");
        setEditRoleModalOpen(false);
        fetchUsers();
      } else {
        toast.error(result.message || "Failed to update role");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update role");
    }
  };

  const handleAddUser = async (userData: IUser) => {
    try {
      const result = await addAUser(userData);
      if (result.status === "success") {
        toast.success("User added successfully");
        setAddUserModalOpen(false);
        fetchUsers();
      } else {
        toast.error(result.message || "Failed to add user");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to add user");
    }
  };

  const handleEditRoleClick = (user: IUser) => {
    setSelectedUser(user);
    setEditRoleModalOpen(true);
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.mobile && u.mobile.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const activeCount = users.filter((u) => u.active).length;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 60%, ${theme.palette.secondary.light} 100%)`,
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 3.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          gap: 2,
          boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Box sx={{ bgcolor: alpha("#fff", 0.15), borderRadius: 2, p: 1, display: "flex", alignItems: "center" }}>
            <ManageAccountsIcon sx={{ color: "#fff", fontSize: { xs: 28, sm: 36 } }} />
          </Box>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
              User Management
            </Typography>
            <Typography variant="body2" sx={{ color: alpha("#fff", 0.8), mt: 0.3 }}>
              Manage system users, roles and access
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddUserModalOpen(true)}
          sx={{
            bgcolor: alpha("#fff", 0.2),
            color: "#fff",
            borderRadius: 2,
            fontWeight: 600,
            textTransform: "none",
            backdropFilter: "blur(4px)",
            border: `1px solid ${alpha("#fff", 0.3)}`,
            flexShrink: 0,
            "&:hover": { bgcolor: alpha("#fff", 0.3) },
            display: { xs: "none", sm: "flex" },
          }}
        >
          Add User
        </Button>
      </Box>

      <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
        {/* Stats + search bar */}
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, sm: 3 },
              py: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search by name, email or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
            />

            {/* Stats chips */}
            <Stack direction="row" spacing={1} ml="auto" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<PeopleAltIcon sx={{ fontSize: "14px !important" }} />}
                label={`${users.length} Total`}
                size="small"
                sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: "secondary.dark", fontWeight: 600 }}
              />
              <Chip
                label={`${activeCount} Active`}
                size="small"
                sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: "success.dark", fontWeight: 600 }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddUserModalOpen(true)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  display: { xs: "flex", sm: "none" },
                  bgcolor: "secondary.main",
                  boxShadow: `0 3px 10px ${alpha(theme.palette.secondary.main, 0.35)}`,
                }}
              >
                Add
              </Button>
            </Stack>
          </Box>

          {/* User grid */}
          <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2.5 }}>
            {loading ? (
              <Grid container spacing={2}>
                {[...Array(6)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <SkeletonCard />
                  </Grid>
                ))}
              </Grid>
            ) : filteredUsers.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  textAlign: "center",
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <PeopleAltIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.3), mb: 1 }} />
                <Typography color="text.secondary">
                  {searchTerm ? "No users match your search." : "No users found."}
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {filteredUsers.map((user) => (
                  <Grid item xs={12} sm={6} md={4} key={user._id}>
                    <UserCard
                      user={user}
                      onEditRole={handleEditRoleClick}
                      onToggleActive={handleToggleActive}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      </Box>

      <AddUserModal
        open={isAddUserModalOpen}
        onClose={() => setAddUserModalOpen(false)}
        onAddUser={handleAddUser}
      />
      <EditUserRoleModal
        open={isEditRoleModalOpen}
        onClose={() => setEditRoleModalOpen(false)}
        user={selectedUser}
        onUpdateRole={handleRoleChange}
      />
    </Box>
  );
};

export default UserManagement;
